import React from 'react';
import Icon from '../../../components/AppIcon';

const DocumentCompletionSummary = ({ players, documents }) => {
  const totalPlayers = players?.length || 0;
  const totalDocumentsRequired = totalPlayers * 5;

  // Ensure documents is always an object, not undefined or null
  const safeDocuments = documents || {};
  
  const uploadedDocuments = Object.values(safeDocuments)?.reduce((count, playerDocs) => {
    if (!playerDocs || typeof playerDocs !== 'object') return count;
    return (count + Object.values(playerDocs)?.filter((doc) => doc !== null && doc !== undefined)?.length);
  }, 0);

  // Prevent division by zero - return 0 if no players or no documents required
  const completionPercentage = totalDocumentsRequired > 0 
    ? Math.round((uploadedDocuments / totalDocumentsRequired) * 100)
    : 0;

  const playersWithCompleteDocuments = players?.filter((player) => {
    const playerDocs = safeDocuments?.[player?.id] || {};
    return Object.keys(playerDocs)?.length === 5 && Object.values(playerDocs)?.every((doc) => doc !== null && doc !== undefined);
  })?.length;

  const getStatusColor = () => {
    if (completionPercentage === 100) return 'text-success';
    if (completionPercentage >= 50) return 'text-warning';
    return 'text-error';
  };

  const getStatusIcon = () => {
    if (completionPercentage === 100) return 'CheckCircle2';
    if (completionPercentage >= 50) return 'Clock';
    return 'AlertCircle';
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">
          Upload Progress
        </h2>
        <div className={`flex items-center gap-2 ${getStatusColor()}`}>
          <Icon name={getStatusIcon()} size={20} />
          <span className="font-semibold">{completionPercentage}%</span>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-muted-foreground">Overall Progress</span>
            <span className="font-medium text-foreground">
              {uploadedDocuments} / {totalDocumentsRequired} documents
            </span>
          </div>
          <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${
                completionPercentage === 100
                  ? 'bg-success'
                  : completionPercentage >= 50
                  ? 'bg-warning' :'bg-error'
              }`}
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-border">
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">
              {totalPlayers}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Total Players
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-success">
              {playersWithCompleteDocuments}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Complete
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-warning">
              {totalPlayers - playersWithCompleteDocuments}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Pending
            </div>
          </div>
        </div>

        {completionPercentage < 100 && (
          <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mt-4">
            <div className="flex gap-3">
              <Icon name="AlertCircle" size={18} className="text-warning flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-foreground mb-1">
                  Documents Pending
                </p>
                <p className="text-muted-foreground">
                  Please upload all required documents for each player before proceeding to the next step.
                </p>
              </div>
            </div>
          </div>
        )}

        {completionPercentage === 100 && (
          <div className="bg-success/10 border border-success/20 rounded-lg p-4 mt-4">
            <div className="flex gap-3">
              <Icon name="CheckCircle2" size={18} className="text-success flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-foreground mb-1">
                  All Documents Uploaded!
                </p>
                <p className="text-muted-foreground">
                  You can now proceed to captain selection and final declaration.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentCompletionSummary;